import { Injectable } from '@angular/core';

export interface WaseelWidgetConfig {
  apiKey?: string;
  theme?: 'light' | 'dark';
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  language?: string;
  customIcon?: string;
  customCSS?: string;
  debug?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class WaseelWidgetConfigService {

  /**
   * Get configuration for the Waseel widget based on environment
   */
  getConfig(environment: 'production' | 'staging'): WaseelWidgetConfig {
    const baseConfig: WaseelWidgetConfig = {
      theme: 'light',
      position: 'bottom-right',
      language: 'en',
      debug: environment === 'staging' // Enable debug in staging
    };

    if (environment === 'production') {
      return {
        ...baseConfig,
      };
    } else {
      return {
        ...baseConfig,
      };
    }
  }

  /**
   * Preload widget assets to prevent 404 errors
   */
  preloadWidgetAssets(): void {
    const assets = [
      // Agent & Button icons (updated)
      'agent-logo.png',
      'btn-widget-general.png',
      
      // Widget display icons
      'send-msg.svg',
      'chat-icon.svg',
      'close-icon.svg',
      'minimize-icon.svg',
      'refresh.svg',
    ];

    assets.forEach(asset => {
      const img = new Image();
      img.src = `/assets/${asset}`;
    });
  }

  /**
   * Apply configuration to the widget
   */
  applyConfig(config: WaseelWidgetConfig): void {
    const windowAny = window as any;

    // Set global configuration
    windowAny.waseelConfig = config;

    // Apply custom CSS
    if (config.customCSS) {
      this.injectCustomCSS(config.customCSS);
    }

    // Try direct widget configuration if available
    if (windowAny.WaseelWidget?.configure) {
      windowAny.WaseelWidget.configure(config);
    }
  }

  /**
   * Inject custom CSS for widget styling
   */
  private injectCustomCSS(css: string): void {
    const existingStyles = document.getElementById('waseel-widget-custom-styles');
    if (existingStyles) {
      existingStyles.remove();
    }
    
    const styleElement = document.createElement('style');
    styleElement.id = 'waseel-widget-custom-styles';
    styleElement.innerHTML = css;
    document.head.appendChild(styleElement);
  }

  /**
   * Get custom CSS for widget styling
   */
  getDefaultCustomCSS(): string {
    return `
      /* Waseel Widget Container Positioning - Prevents Misalignment */
      [data-waseel-widget],
      .waseel-widget,
      #waseel-widget,
      [class*="waseel"],
      iframe[src*="waseel"],
      iframe[src*="chat"],
      iframe[src*="widget"] {
        position: fixed !important;
        bottom: 20px !important;
        right: 20px !important;
        z-index: 9999 !important;
      }

      /* RTL Support */
      [dir="rtl"] [data-waseel-widget],
      [dir="rtl"] .waseel-widget,
      [dir="rtl"] #waseel-widget,
      [dir="rtl"] [class*="waseel"],
      [dir="rtl"] iframe[src*="waseel"],
      [dir="rtl"] iframe[src*="chat"],
      [dir="rtl"] iframe[src*="widget"] {
        right: auto !important;
        left: 20px !important;
      }

      /* Waseel Widget Custom Colors - Simple & Clean */
      [data-waseel-widget] .widget-button,
      .waseel-widget .widget-button,
      #waseel-widget .widget-button,
      [class*="chat"] button,
      [class*="widget"] button {
        background-color: rgba(21, 84, 107, 1) !important;
        background-size: 40px 40px !important;
        background-repeat: no-repeat !important;
        background-position: center !important;
        width: 60px !important;
        height: 60px !important;
        border-radius: 50% !important;
        border: none !important;
        box-shadow: 0 4px 12px rgba(37, 150, 190, 0.3) !important;
        transition: all 0.3s ease !important;
        color: #ffffff !important;
        cursor: pointer !important;
      }

      [data-waseel-widget] .widget-button:hover,
      .waseel-widget .widget-button:hover,
      #waseel-widget .widget-button:hover,
      [class*="chat"] button:hover,
      [class*="widget"] button:hover {
        background-color: rgba(21, 84, 107, 1) !important;
        transform: scale(1.1) !important;
        box-shadow: 0 6px 16px rgba(37, 150, 190, 0.4) !important;
      }

      [class*="chat-window"],
      [class*="widget-window"] {
        background-color: #ffffff !important;
        border: 1px solid #e0e0e0 !important;
        border-radius: 12px !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
      }

      [class*="chat-header"],
      [class*="widget-header"] {
        background-color: rgba(21, 84, 107, 1)!important;
        color: #ffffff !important;
        border-radius: 12px 12px 0 0 !important;
      }

      [class*="message-bubble"],
      [class*="send-button"] {
        background-color: rgba(21, 84, 107, 1)!important;
        color: #ffffff !important;
        border-radius: 8px !important;
      }

      [class*="message-user"] [class*="message-text"] {
        background-color: rgba(21, 84, 107, 1) !important;
        color: #ffffff !important;
        border: none !important;
        border-radius: 8px !important;
      }

      [class*="message-agent"] [class*="message-text"] {
        background-color: #f5f5f5 !important;
        color: #333333 !important;
        border: none !important;
        border-radius: 8px !important;
      }

      [class*="chat-input"] input {
        background-color: #ffffff !important;
        border: 1px solid #e0e0e0 !important;
        border-radius: 6px !important;
        color: #333333 !important;
        transition: border-color 0.2s ease !important;
      }

      [class*="chat-input"] input:focus {
        border-color: rgba(21, 84, 107, 1) !important;
        box-shadow: 0 0 4px rgba(37, 150, 190, 0.2) !important;
        outline: none !important;
      }

      [data-waseel-widget] img[src=""] {
        display: none !important;
      }

      /* Smooth scrollbar for chat */
      [class*="chat-messages"],
      [class*="message-list"] {
        scrollbar-width: thin !important;
        scrollbar-color: rgba(21, 84, 107, 1) #f5f5f5 !important;
      }

      [class*="chat-messages"]::-webkit-scrollbar,
      [class*="message-list"]::-webkit-scrollbar {
        width: 6px !important;
      }

      [class*="chat-messages"]::-webkit-scrollbar-track,
      [class*="message-list"]::-webkit-scrollbar-track {
        background: #f5f5f5 !important;
      }

      [class*="chat-messages"]::-webkit-scrollbar-thumb,
      [class*="message-list"]::-webkit-scrollbar-thumb {
        background: rgba(21, 84, 107, 1) !important;
        border-radius: 3px !important;
      }

      [class*="chat-messages"]::-webkit-scrollbar-thumb:hover,
      [class*="message-list"]::-webkit-scrollbar-thumb:hover {
        background: rgba(21, 84, 107, 1) !important;
      }
    `;
  }
}